import React, { useContext, useState, useEffect } from 'react';
import { Alert, CircularProgress, Snackbar } from '@mui/material';
import { Add, CloseRounded, KeyboardArrowDownRounded } from '@mui/icons-material';

import ModalContext from '../../../context/ModalContext.js';
import {
  createAuthor, createTag, createTopic,
  listAuthors, listTags, listTopics,
  slugify, uniqueSlug, updatePost,
} from '../../blogApi';

/*
 * One modal drives authors, topics and tags. deliva-api attached each of them
 * one at a time through its own join endpoints, so this was three near-identical
 * files; blog-api replaces the whole set in a single PATCH, which is the same
 * shape for all three.
 *
 * Authors are single-select: a post stores an ordered list but the read contract
 * only returns the primary one, so a multi-select here could not round-trip and
 * would quietly drop co-authors on the next save.
 */
const KINDS = {
  authors: {
    heading: 'Edit Blog Author',
    field: 'authorSlugs',
    single: true,
    required: true,
    list: listAuthors,
    create: (name) => createAuthor({ slug: name.slug, name: name.name }),
    selectedFrom: (post) => (post?.author ? [post.author] : []),
  },
  topics: {
    heading: 'Edit Blog Topics',
    field: 'topicSlugs',
    single: false,
    required: false,
    list: listTopics,
    create: (name) => createTopic({ slug: name.slug, name: name.name }),
    selectedFrom: (post) => post?.topics ?? [],
  },
  tags: {
    heading: 'Edit Blog Tags',
    field: 'tagSlugs',
    single: false,
    required: false,
    list: listTags,
    create: (name) => createTag({ slug: name.slug, name: name.name }),
    selectedFrom: (post) => post?.tags ?? [],
  },
};

const EditBlogTermsModal = ({ modalDetails, kind }) => {

  const config = KINDS[kind];
  const modalCtx = useContext(ModalContext);

  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState(() => config.selectedFrom(modalDetails));
  const [searchItem, setSearchItem] = useState('');
  const [showItemResults, setShowItemResults] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { getAllItems(); }, []);

  useEffect(() => {
    setSelectedItems(config.selectedFrom(modalDetails));
  }, [modalDetails]);

  useEffect(() => {
    setShowItemResults((searchItem?.length ?? 0) > 0);
  }, [searchItem]);

  const getAllItems = async () => {
    try {
      setItems((await config.list()) ?? []);
    } catch (err) {
      console.error(err);
      setError(err.message);
    }
  };

  // Nothing is written until Save; blog-api replaces the set in one request.
  const handleSelectItem = (item) => {
    setSearchItem('');
    setShowItemResults(false);
    if (config.single) {
      setSelectedItems([item]);
      return;
    }
    setSelectedItems((prev) => (prev.some((el) => el.slug === item.slug) ? prev : [...prev, item]));
  };

  const handleCreateNewItem = async (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return;

    if (items.some((item) => item.name.toLowerCase() === trimmed.toLowerCase())) {
      setSnackbar('Item already exists.');
      return;
    }
    if (!slugify(trimmed)) {
      setSnackbar('That name has no letters or numbers to build a URL from.');
      return;
    }

    try {
      const created = await config.create({
        slug: uniqueSlug(trimmed, items.map((i) => i.slug)),
        name: trimmed,
      });
      setItems((prev) => [...prev, created]);
      handleSelectItem(created);
    } catch (err) {
      console.error(err);
      setError(err.message);
    }
  };

  const handleFilterArray = (slug) => {
    setSelectedItems((prev) => prev.filter((item) => item.slug !== slug));
  };

  const handleSave = async () => {
    if (config.required && selectedItems.length === 0) {
      setError('A post must always credit an author.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await updatePost(modalDetails.slug, { [config.field]: selectedItems.map((item) => item.slug) });
      modalCtx.setPageUpdate({ page: 'Blogs' });
      modalCtx.closeModal();
    } catch (err) {
      console.error(err);
      setError(err.message);
      setSaving(false);
    }
  };

  const term = (searchItem || '').toLowerCase();
  const matches = items.filter((item) => (item?.name || '').toLowerCase().includes(term));

  return (
    <div className="select-item-modal">
      <Snackbar open={snackbar !== null} autoHideDuration={3000} onClose={() => setSnackbar(null)} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity="info" sx={{ width: '100%' }}>{snackbar}</Alert>
      </Snackbar>

      <header>
        <h1 data-aos="fade-right" data-aos-delay="500">{config.heading}</h1>
      </header>

      <section>
        <div className="search-item-container">
          <input type="text" placeholder='Search for item...' value={searchItem} onChange={(e) => setSearchItem(e.target.value)} />
          <KeyboardArrowDownRounded className='drop-down-btn' onClick={() => setShowItemResults((v) => !v)} style={showItemResults ? { transform: 'rotateZ(180deg)' } : null} />
        </div>

        <div className="items-menu" style={showItemResults ? null : { display: 'none' }}>
          <ul>
            {matches.map((item) => (
              <li key={item.slug} onClick={() => handleSelectItem(item)}>{item.name}</li>
            ))}
            {searchItem?.length === 0 ? null : (
              <li className='add-new-item' onClick={() => handleCreateNewItem(searchItem)}>
                <Add /> &nbsp; Add new item &nbsp; <span className='item-text'>"{searchItem}"</span>
              </li>
            )}
          </ul>
        </div>

        <div className="item-list">
          {selectedItems.map((item) => (
            <span key={item.slug} className='item'>
              {item.name}
              <CloseRounded className='remove-item' onClick={() => handleFilterArray(item.slug)} />
            </span>
          ))}
        </div>

        {error ? <p style={{ color: '#d32f2f', fontSize: '0.85rem' }}>{error}</p> : null}

        <div className="save-changes-btn">
          <button className='btn-primary' disabled={saving} onClick={handleSave}>
            {saving ? <CircularProgress size={20} style={{ color: '#fff' }} /> : 'Save Changes'}
          </button>
          <button className='btn-primary-border' onClick={() => modalCtx.closeModal()}>Close</button>
        </div>
      </section>
    </div>
  );
};

export default EditBlogTermsModal;
